// Centralized aesthetic definitions for icon hover/interaction states.
// Any new icon hover style should be added here as a factory instead of being redefined inline at the call site.

export const ICON_HOVER_TRANSITION = 'all 0.2s ease';

/** Reference glyph size for the action icons in the page header (pin, grid/carousel toggle, volume, ...). */
export const HEADER_ACTION_ICON_SIZE = 24;

/** Reference size for social platform brand icons (Facebook, YouTube, TikTok, Telegram, ...). */
export const SOCIAL_ICON_SIZE = 24;

/**
 * Circular translucent hover background for toggle icon buttons (pin, visibility, delete, ...).
 * Set `active`/`activeBg` for toggles that turn solid when "on" (e.g. an applied filter).
 */
export const circularIconHoverSx = ({
    size = 30,
    color = 'white',
    bg = 'transparent',
    hoverBg = 'rgba(255,255,255,0.15)',
    border,
    // MUI's sx multiplies a bare number by theme.shape.borderRadius (4 by default), so a plain
    // `6` becomes 24px and renders as a full circle on a 30-40px button. Use an explicit unit.
    borderRadius = '4px !important',
    active = false,
    activeBg = 'orange',
} = {}) => ({
    color,
    borderRadius,
    width: size,
    height: size,
    backgroundColor: active ? activeBg : bg,
    ...(border ? { border } : {}),
    '&:hover': {
        backgroundColor: active ? activeBg : hoverBg,
    },
});

/** Raster/SVG icon rendered via <Box component="img" sx={imageIconHoverSx()} />, dimmed until hovered. */
export const imageIconHoverSx = ({
    size = 32,
    opacity = 0.65,
    hoverOpacity = 1,
    hoverScale = 0.85,
} = {}) => ({
    width: size,
    height: size,
    opacity,
    filter: 'brightness(0) invert(1)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
        opacity: hoverOpacity,
        transform: `scale(${hoverScale})`,
    },
});

/** Larger variant of imageIconHoverSx, used for fullscreen player controls. */
export const fsImageIconHoverSx = (overrides = {}) => imageIconHoverSx({
    size: 100,
    opacity: 0.8,
    hoverScale: 0.9,
    ...overrides,
});

/** Transparent IconButton wrapper for image icons above: no own hover feedback, the inner image handles it. */
export const transparentIconButtonSx = ({ size } = {}) => ({
    backgroundColor: 'transparent',
    p: 0,
    ...(size ? { width: size, height: size } : {}),
    '&:hover': { backgroundColor: 'transparent' },
});

/** "Glass" icon button: translucent panel that lights up and scales slightly on hover. */
export const glassIconHoverSx = ({
    size = 72,
    borderRadius = '20px',
    hoverScale = 1.05,
} = {}) => ({
    width: size,
    height: size,
    borderRadius,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        border: '2px solid rgba(255, 255, 255, 0.7)',
        transform: `scale(${hoverScale})`,
    },
});

/** Icon that swaps to a plain color on hover, e.g. drag handles and other secondary icon affordances. */
export const colorSwapIconHoverSx = ({
    color = '#555',
    hoverColor = '#fff',
} = {}) => ({
    color,
    transition: ICON_HOVER_TRANSITION,
    '&:hover': { color: hoverColor },
});

/** Compact text+icon hover pill for inline actions (e.g. a "Flip"/"Reset" label next to a small icon). */
export const iconLabelHoverSx = ({
    hoverBg = 'rgba(0,0,0,0.6)',
    borderRadius = '4px !important',
    p = 0.5,
} = {}) => ({
    cursor: 'pointer',
    borderRadius,
    p,
    '&:hover': { backgroundColor: hoverBg },
});

/** Orange dashed-underline inline text link (e.g. "Include All" / "Reset"), the app's inline filter-action link style. */
export const dashedLinkSx = ({
    disabled = false,
    color = 'primary.main',
    disabledColor = 'text.disabled',
    hoverColor = 'primary.dark',
} = {}) => ({
    color: disabled ? disabledColor : color,
    cursor: disabled ? 'default' : 'pointer',
    textDecoration: 'underline',
    textDecorationStyle: 'dashed',
    textUnderlineOffset: '3px',
    fontWeight: 500,
    '&:hover': {
        color: disabled ? disabledColor : hoverColor,
    },
});

/** Small circular icon "badge" used as a Button's startIcon (e.g. Add/Edit). Resting style only — pair with iconBadgeHoverSx on the wrapping Button's `&:hover`. */
export const iconBadgeSx = ({
    size = 25,
    bg = 'white',
    color = 'black',
} = {}) => ({
    borderRadius: '50%',
    bgcolor: bg,
    color,
    height: `${size}px`,
    width: `${size}px`,
    transition: '0.2s',
});

/** Hover-state color inversion for an iconBadgeSx badge; merge into the wrapping Button's `&:hover` sx (targets the badge via its `add-icon` className). */
export const iconBadgeHoverSx = ({
    hoverBg = 'orange',
    hoverColor = 'white',
} = {}) => ({
    '& .add-icon': { bgcolor: hoverBg, color: hoverColor },
});
